// frontend/src/components/dashboard/GraficoFinanceiro12m.jsx
import React, { useMemo } from 'react';
import {
  ResponsiveContainer,
  ComposedChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Bar,
  Line,
} from 'recharts';
import { Activity } from 'lucide-react';

/* =======================
   PALETA 02 — AURORA
======================= */
const AURORA = {
  emerald: '#34D399',
  purple: '#A78BFA',
  grid: 'rgba(255,255,255,0.08)',
  text: 'rgba(255,255,255,0.90)',
  text2: 'rgba(255,255,255,0.65)',
  text3: 'rgba(255,255,255,0.45)',
  border: 'rgba(255,255,255,0.10)',
  cardBg: 'rgba(9,9,11,0.80)',
};

const formatMoney = (val) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  }).format(Number(val || 0));

const MESES_LABEL = [
  'Jan',
  'Fev',
  'Mar',
  'Abr',
  'Mai',
  'Jun',
  'Jul',
  'Ago',
  'Set',
  'Out',
  'Nov',
  'Dez',
];

function parseMesIndex(mesRaw) {
  if (mesRaw == null) return null;

  // aceita:
  // - "2026-02"
  // - "02"
  // - 2
  const s = String(mesRaw);

  if (s.includes('-')) {
    const parts = s.split('-');
    const mesStr = parts[1];
    const idx = Number(mesStr) - 1;
    return Number.isFinite(idx) ? idx : null;
  }

  const idx = Number(s) - 1;
  return Number.isFinite(idx) ? idx : null;
}

function movingAvg3(arr, i, key) {
  const a = Number(arr[i - 2]?.[key] || 0);
  const b = Number(arr[i - 1]?.[key] || 0);
  const c = Number(arr[i]?.[key] || 0);
  const denom = i >= 2 ? 3 : i === 1 ? 2 : 1;
  return (a + b + c) / denom;
}

function pctVar(prev, cur) {
  const P = Number(prev || 0);
  const C = Number(cur || 0);
  if (P <= 0 && C <= 0) return null;
  if (P <= 0 && C > 0) return 100;
  return ((C - P) / P) * 100;
}

function TooltipAurora({ active, payload, label }) {
  if (!active || !payload?.length) return null;

  const d = payload?.[0]?.payload || {};
  const vgv = Number(d?.vgv || 0);
  const honor = Number(d?.honorarios || 0);
  const varPct = d?.varPct;

  return (
    <div
      className="rounded-2xl border p-3 shadow-[0_14px_40px_rgba(0,0,0,0.55)] backdrop-blur"
      style={{
        borderColor: AURORA.border,
        background: 'rgba(9,9,11,0.92)',
        color: AURORA.text,
      }}
    >
      <div
        className="text-[11px] font-semibold uppercase tracking-[0.14em]"
        style={{ color: AURORA.text3 }}
      >
        {label}
      </div>

      <div className="mt-1 text-lg font-semibold">{formatMoney(vgv)}</div>
      <div className="text-xs" style={{ color: AURORA.text2 }}>
        VGV no mês
      </div>

      <div className="mt-2 space-y-1 text-xs">
        <div className="flex items-center justify-between gap-6">
          <span style={{ color: AURORA.text2 }}>Honorários (mês)</span>
          <b style={{ color: AURORA.emerald }}>{formatMoney(honor)}</b>
        </div>

        {typeof varPct === 'number' && (
          <div className="flex items-center justify-between gap-6">
            <span style={{ color: AURORA.text2 }}>Vs mês anterior</span>
            <b style={{ color: AURORA.text }}>
              {varPct > 0 ? '+' : ''}
              {varPct.toFixed(0)}%
            </b>
          </div>
        )}
      </div>
    </div>
  );
}

export default function GraficoFinanceiro12m({
  loading,
  dataMensal = [],
  vgvTotal = 0,
  honorariosTotal = 0,
  dark = true,
  compact = false,

  // ✅ NOVO: ano do histórico (ex: 2026)
  year,

  // ✅ slot opcional no canto direito do header (ex: filtro de ano)
  rightSlot = null,
}) {
  const dados = useMemo(() => {
    // ✅ Mantém 12 colunas (Jan-Dez), mas consome o que vier do dashboardService
    // Esperado: { mes:"YYYY-MM", vgv, comissao, quantidade }
    const base = MESES_LABEL.map((m) => ({ mes: m, vgv: 0, honorarios: 0 }));

    (dataMensal || []).forEach((b) => {
      const idx = parseMesIndex(b?.mes);
      if (idx == null || idx < 0 || idx > 11) return;

      const vgv = Number(b?.vgv ?? b?.valor ?? b?.total ?? 0) || 0;
      const honor = Number(b?.comissao ?? b?.honorarios ?? 0) || 0;

      base[idx].vgv += vgv;
      base[idx].honorarios += honor;
    });

    return base.map((row, i) => {
      const prev = base[i - 1]?.vgv ?? 0;
      const cur = row.vgv ?? 0;
      return {
        ...row,
        media3: movingAvg3(base, i, 'vgv'),
        varPct: pctVar(prev, cur),
      };
    });
  }, [dataMensal]);

  const totalAno = useMemo(
    () => dados.reduce((acc, cur) => acc + (Number(cur.vgv) || 0), 0),
    [dados]
  );

  const totalHonorAno = useMemo(
    () => dados.reduce((acc, cur) => acc + (Number(cur.honorarios) || 0), 0),
    [dados]
  );

  const vgv = vgvTotal || totalAno;
  const honor = honorariosTotal || totalHonorAno;

  const pad = compact ? 'p-5' : 'p-6';
  const chartH = compact ? 'h-72' : 'h-80';

  const shell = dark
    ? `relative overflow-hidden rounded-3xl border ${pad} shadow-2xl backdrop-blur`
    : `relative overflow-hidden rounded-3xl border border-zinc-200/70 bg-white/80 ${pad} shadow-[0_14px_40px_rgba(0,0,0,0.08)] backdrop-blur`;

  return (
    <div
      className={shell}
      style={
        dark ? { borderColor: AURORA.border, background: AURORA.cardBg } : null
      }
    >
      {/* AURORA HALOS */}
      <div
        className="pointer-events-none absolute -right-28 -top-28 h-80 w-80 rounded-full blur-3xl"
        style={{ background: 'rgba(167,139,250,0.22)' }}
      />
      <div
        className="pointer-events-none absolute -left-28 -bottom-28 h-80 w-80 rounded-full blur-3xl"
        style={{ background: 'rgba(52,211,153,0.18)' }}
      />

      <div className="relative">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div
              className={`grid place-items-center rounded-2xl border shadow-[inset_0_0_0_1px_rgba(16,185,129,0.10)] ${
                compact ? 'h-10 w-10' : 'h-11 w-11'
              }`}
              style={{
                borderColor: AURORA.border,
                background:
                  'linear-gradient(135deg, rgba(52,211,153,0.14), rgba(167,139,250,0.12))',
              }}
            >
              <Activity className="h-5 w-5" style={{ color: AURORA.text2 }} />
            </div>

            <div>
              <div
                className={`${compact ? 'text-base' : 'text-lg'} font-semibold`}
                style={{ color: dark ? AURORA.text : undefined }}
              >
                Performance Financeira
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <div
                  className={`${compact ? 'text-xs' : 'text-sm'}`}
                  style={{ color: dark ? AURORA.text2 : undefined }}
                >
                  Histórico anual (Jan–Dez): VGV (barras) + tendência (linha)
                </div>

                {year ? (
                  <span
                    className="rounded-full border px-2 py-0.5 text-[10px] font-extrabold"
                    style={{
                      borderColor: AURORA.border,
                      background: 'rgba(255,255,255,0.06)',
                      color: AURORA.text2,
                    }}
                  >
                    Ano {year}
                  </span>
                ) : null}
              </div>
            </div>
          </div>

          {/* ✅ slot pro filtro de ano (ou chips) */}
          <div className="flex items-center gap-2">{rightSlot}</div>
        </div>

        <div
          className={`${compact ? 'mt-3' : 'mt-4'} flex flex-wrap items-center gap-2.5 text-sm`}
        >
          <div
            className="rounded-2xl border px-3.5 py-2"
            style={{
              borderColor: AURORA.border,
              background: 'rgba(255,255,255,0.05)',
            }}
          >
            <span style={{ color: AURORA.text3 }}>
              VGV acumulado{year ? ` (${year})` : ''}:
            </span>{' '}
            <span className="font-semibold" style={{ color: AURORA.text }}>
              {formatMoney(vgv)}
            </span>
          </div>

          <div
            className="rounded-2xl border px-3.5 py-2"
            style={{
              borderColor: 'rgba(52,211,153,0.20)',
              background: 'rgba(52,211,153,0.10)',
            }}
          >
            <span style={{ color: AURORA.text2 }}>
              Honorários (estimado){year ? ` (${year})` : ''}:
            </span>{' '}
            <span className="font-semibold" style={{ color: AURORA.emerald }}>
              {formatMoney(honor)}
            </span>
          </div>
        </div>

        <div className={`${compact ? 'mt-4' : 'mt-5'} ${chartH}`}>
          {loading ? (
            <div
              className="flex h-full items-center justify-center text-sm"
              style={{ color: AURORA.text2 }}
            >
              Carregando gráfico...
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={dados}
                margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="auroraBar" x1="0" y1="0" x2="0" y2="1">
                    <stop
                      offset="0%"
                      stopColor={AURORA.emerald}
                      stopOpacity="0.75"
                    />
                    <stop
                      offset="100%"
                      stopColor={AURORA.emerald}
                      stopOpacity="0.22"
                    />
                  </linearGradient>

                  <filter id="auroraGlow">
                    <feGaussianBlur stdDeviation="4" result="blur" />
                    <feMerge>
                      <feMergeNode in="blur" />
                      <feMergeNode in="SourceGraphic" />
                    </feMerge>
                  </filter>
                </defs>

                <CartesianGrid
                  strokeDasharray="6 14"
                  vertical={false}
                  stroke={AURORA.grid}
                />

                <XAxis
                  dataKey="mes"
                  tick={{ fill: 'rgba(255,255,255,0.60)', fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                  padding={{ left: 10, right: 10 }}
                  tickMargin={8}
                />

                <YAxis
                  tick={{ fill: 'rgba(255,255,255,0.55)', fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                  width={46}
                  tickFormatter={(v) => {
                    const n = Number(v || 0);
                    if (n >= 1000000) return `R$ ${(n / 1000000).toFixed(1)}M`;
                    if (n >= 1000) return `R$ ${Math.round(n / 1000)}k`;
                    return `R$ ${n}`;
                  }}
                />

                <Tooltip content={<TooltipAurora />} />

                <Bar
                  dataKey="vgv"
                  radius={[14, 14, 0, 0]}
                  fill="url(#auroraBar)"
                />

                <Line
                  type="monotone"
                  dataKey="media3"
                  stroke={AURORA.purple}
                  strokeWidth={3}
                  dot={false}
                  filter="url(#auroraGlow)"
                />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
}
